import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Modal,
  TextInput,
  Alert,
  SafeAreaView,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useCategoria } from '@/contexts/CategoriaContext';
import { Categoria } from '@/types/app';
import { categoriasPadrao } from '@/constants/categorias';

interface CategoriaSelectorProps {
  categoriaSelecionada: Categoria;
  onCategoriaChange: (categoria: Categoria) => void;
  categoriasDisponiveis?: Categoria[];
  permitirCriar?: boolean;
}

export default function CategoriaSelector({
  categoriaSelecionada,
  onCategoriaChange,
  categoriasDisponiveis = categoriasPadrao,
  permitirCriar = false,
}: CategoriaSelectorProps) {
  const { colors } = useTheme();
  const { todasCategorias, adicionarCategoria } = useCategoria();
  const [showModal, setShowModal] = useState(false);
  const [novaCategoria, setNovaCategoria] = useState({ nome: '', emoji: '📦' });

  const emojisDisponiveis = ['🍕', '🚗', '🏨', '🎬', '🛍️', '💊', '📚', '📦', '✈️', '🏖️', '🍻', '🎉', '🏋️', '🎮', '🎨', '🎵'];

  const criarCategoriaCustomizada = async () => {
    if (!novaCategoria.nome.trim()) {
      Alert.alert('Erro', 'Digite um nome para a categoria');
      return;
    }

    const categoriaExistente = todasCategorias.find(c => 
      c.nome.toLowerCase() === novaCategoria.nome.trim().toLowerCase()
    );

    if (categoriaExistente) {
      Alert.alert('Erro', 'Já existe uma categoria com este nome');
      return;
    }

    const novaCategoriaObj: Categoria = {
      id: `custom_${Date.now()}`,
      nome: novaCategoria.nome.trim(),
      emoji: novaCategoria.emoji,
      cor: '#DDA0DD', // Cor padrão para categorias customizadas
      icone: 'Package',
    };

    try {
      await adicionarCategoria(novaCategoriaObj);
      setNovaCategoria({ nome: '', emoji: '📦' });
      setShowModal(false);
      Alert.alert('Sucesso', 'Categoria criada com sucesso!');
    } catch {
      Alert.alert('Erro', 'Falha ao criar categoria');
    }
  };

  const styles = StyleSheet.create({
    container: {
      marginBottom: 24,
    },
    label: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 12,
    },
    categoriaAtual: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
      gap: 12,
    },
    categoriaEmoji: {
      fontSize: 24,
    },
    categoriaNome: {
      flex: 1,
      fontSize: 16,
      fontFamily: 'Inter-Medium',
      color: colors.text,
    },
    modalContainer: {
      flex: 1,
      backgroundColor: colors.background,
    },
    modalHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 24,
      paddingVertical: 16,
      backgroundColor: colors.surface,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    modalTitle: {
      fontSize: 18,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
    },
    modalCloseButton: {
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.textSecondary,
    },
    modalContent: {
      flex: 1,
      paddingHorizontal: 24,
      paddingTop: 24,
    },
    categoriasGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 24,
    },
    categoriaOption: {
      width: '48%',
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 16,
      borderWidth: 2,
      borderColor: colors.border,
      alignItems: 'center',
      gap: 8,
    },
    categoriaOptionSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    categoriaOptionEmoji: {
      fontSize: 32,
    },
    categoriaOptionNome: {
      fontSize: 14,
      fontFamily: 'Inter-Medium',
      color: colors.text,
      textAlign: 'center',
    },
    categoriaOptionNomeSelected: {
      color: colors.primary,
    },
    criarCategoriaSection: {
      marginTop: 24,
      paddingTop: 24,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    criarCategoriaTitle: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 16,
    },
    inputGroup: {
      marginBottom: 16,
    },
    inputLabel: {
      fontSize: 14,
      fontFamily: 'Inter-SemiBold',
      color: colors.text,
      marginBottom: 8,
    },
    input: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      paddingHorizontal: 16,
      height: 48,
      fontSize: 16,
      fontFamily: 'Inter-Regular',
      color: colors.text,
      borderWidth: 1,
      borderColor: colors.border,
    },
    emojiSelector: {
      marginBottom: 16,
    },
    emojiList: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
    },
    emojiOption: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.surface,
      justifyContent: 'center',
      alignItems: 'center',
      borderWidth: 2,
      borderColor: 'transparent',
    },
    emojiSelected: {
      borderColor: colors.primary,
      backgroundColor: colors.primary + '20',
    },
    emojiOptionText: {
      fontSize: 20,
    },
    criarButton: {
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 16,
      alignItems: 'center',
    },
    criarButtonText: {
      fontSize: 16,
      fontFamily: 'Inter-SemiBold',
      color: '#FFFFFF',
    },
  });

  return (
    <View style={styles.container}>
      <Text style={styles.label}>Categoria</Text>
      
      <TouchableOpacity
        style={styles.categoriaAtual}
        onPress={() => setShowModal(true)}
        activeOpacity={0.7}
      >
        <Text style={styles.categoriaEmoji}>{categoriaSelecionada.emoji}</Text>
        <Text style={styles.categoriaNome}>{categoriaSelecionada.nome}</Text>
      </TouchableOpacity>

      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Selecionar Categoria</Text>
            <TouchableOpacity onPress={() => setShowModal(false)}>
              <Text style={styles.modalCloseButton}>Fechar</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            <View style={styles.categoriasGrid}>
              {todasCategorias.map((categoria) => (
                <TouchableOpacity
                  key={categoria.id}
                  style={[
                    styles.categoriaOption,
                    categoriaSelecionada.id === categoria.id && styles.categoriaOptionSelected
                  ]}
                  onPress={() => {
                    onCategoriaChange(categoria);
                    setShowModal(false);
                  }}
                >
                  <Text style={styles.categoriaOptionEmoji}>{categoria.emoji}</Text>
                  <Text style={[
                    styles.categoriaOptionNome,
                    categoriaSelecionada.id === categoria.id && styles.categoriaOptionNomeSelected
                  ]}>
                    {categoria.nome}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {permitirCriar && (
              <View style={styles.criarCategoriaSection}>
                <Text style={styles.criarCategoriaTitle}>Criar nova categoria</Text>
                
                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Nome da categoria</Text>
                  <TextInput
                    style={styles.input}
                    placeholder="Ex: Lazer, Trabalho, etc."
                    placeholderTextColor={colors.textSecondary}
                    value={novaCategoria.nome}
                    onChangeText={(text) => setNovaCategoria(prev => ({ ...prev, nome: text }))}
                    maxLength={20}
                  />
                </View>

                <View style={styles.emojiSelector}>
                  <Text style={styles.inputLabel}>Escolher emoji</Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <View style={styles.emojiList}>
                      {emojisDisponiveis.map((emoji) => (
                        <TouchableOpacity
                          key={emoji}
                          style={[
                            styles.emojiOption,
                            novaCategoria.emoji === emoji && styles.emojiSelected
                          ]}
                          onPress={() => setNovaCategoria(prev => ({ ...prev, emoji }))}
                        >
                          <Text style={styles.emojiOptionText}>{emoji}</Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                </View>

                <TouchableOpacity
                  style={styles.criarButton}
                  onPress={criarCategoriaCustomizada}
                  activeOpacity={0.8}
                >
                  <Text style={styles.criarButtonText}>Criar Categoria</Text>
                </TouchableOpacity>
              </View>
            )}
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </View>
  );
}